import { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Shield } from "lucide-react";
import PortalShellPageHeader from "../../components/Layout/PortalShellPageHeader/PortalShellPageHeader";
import portalStyles from "../../layout/portalPageShell.module.css";
import ProductsServicesPanel from "../../components/Layout/ProductsServicesPanel/ProductsServicesPanel";
import ServiceMonitoring from "../../components/Layout/ServiceMonitoring/ServiceMonitoring";
import PortalUserStrip from "../../components/Layout/PortalUserStrip/PortalUserStrip";
import { useAuth } from "../../context/authContext";
import { getSubscriptionPlans } from "../../services/subscription-plan-service";
import { getDomainPlans } from "../../services/domain-plan-service";

export default function AdminPage() {
  const { user } = useAuth();
  const location = useLocation();
  const { t } = useTranslation("dashboard");
  const [plans, setPlans] = useState([]);
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      if (!user) {
        setPlans([]);
        setDomains([]);
        return;
      }

      const [plansData, domainsData] = await Promise.all([
        getSubscriptionPlans(),
        getDomainPlans(),
      ]);
      setPlans(plansData.data || []);
      setDomains(domainsData.data || []);
    } catch {
      setPlans([]);
      setDomains([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchData();
  }, [fetchData, location.key]);

  useEffect(() => {
    if (location.pathname !== "/admin") return;

    const onVisibility = () => {
      if (document.visibilityState === "visible" && user) {
        fetchData();
      }
    };

    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [fetchData, location.pathname, user]);

  const { numberOfWebsites, numberOfMobileApps, numberOfSubscriptions } =
    plans.reduce(
      (acc, plan) => {
        if (plan.projectType === "Web Application") acc.numberOfWebsites += 1;
        if (plan.projectType === "Mobile Application")
          acc.numberOfMobileApps += 1;
        if (plan.status === "active") acc.numberOfSubscriptions += 1;
        return acc;
      },
      { numberOfWebsites: 0, numberOfMobileApps: 0, numberOfSubscriptions: 0 }
    );

  const productsData = {
    numberOfWebsites,
    numberOfDomains: domains.length,
    numberOfMobileApps,
    numberOfSubscriptions,
    subscriptionPlan: t("productsPanel.adminSubscriptionBadge"),
  };

  const headerIcon = (
    <Shield size={28} strokeWidth={1.75} absoluteStrokeWidth />
  );

  if (loading) {
    return (
      <div className={portalStyles.portalPage}>
        <div className={portalStyles.container}>
          <PortalShellPageHeader
            icon={headerIcon}
            title={t("adminTitle")}
            subtitle={t("loadingData")}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={portalStyles.portalPage}>
      <div className={portalStyles.container}>
        <PortalShellPageHeader
          icon={headerIcon}
          title={t("adminTitle")}
          subtitle={t("monitorClients")}
        />

        {user ? (
          <section
            className={portalStyles.userStripSection}
            aria-label={t("portalUser.sectionLabel")}
          >
            <PortalUserStrip user={user} />
          </section>
        ) : null}

        <section
          className={portalStyles.overviewSection}
          aria-labelledby="admin-overview-heading"
        >
          <div className={portalStyles.overviewScale}>
            <h2
              id="admin-overview-heading"
              className={portalStyles.sectionLabel}
            >
              {t("sectionOverview")}
            </h2>
            <div className={portalStyles.overviewProducts}>
              <ProductsServicesPanel products={productsData} />
            </div>
          </div>
        </section>

        <section
          className={portalStyles.monitoringSection}
          aria-label={t("sectionMonitoring")}
        >
          <ServiceMonitoring plans={plans} domains={domains} />
        </section>
      </div>
    </div>
  );
}
